import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { SessionsAccordionProps } from "./Types";
import { Translate } from "@/app/application/Translate";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Chip,
	Divider,
	LinearProgress,
	List,
	ListItemButton,
	Paper,
	Stack,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";

const SessionsAccordion: React.FC<SessionsAccordionProps> = ({
	items,
	isLoading,
}) => {
	const theme = useTheme();
	const [expandedPanels, setExpandedPanels] = useState<string[]>([]);

	const safeItems = Array.isArray(items) ? items : [];
	const allExpanded = useMemo(
		() => safeItems.every((u) => expandedPanels.includes(`panel-${u.UserId}`)),
		[safeItems, expandedPanels],
	);

	type UserMeta = {
		userId: number;
		lastDate: Date | null;
		daysSinceLast: number | null;
		overdueCount: number;
		status: "ok" | "behind" | "risk";
	};

	const metaById = useMemo(() => {
		const map = new Map<number, UserMeta>();
		const today = dayjs();

		for (const u of safeItems) {
			const sessions = Array.isArray(u.Sessions) ? u.Sessions : [];
			const dates: Date[] = [];
			let overdueCount = 0;

			for (const s of sessions) {
				const d = new Date(s.SessionDate);
				if (Number.isNaN(d.getTime())) continue;
				dates.push(d);
				const daysAgo = today.diff(dayjs(d), "day");
				if (daysAgo > 0) overdueCount++;
			}

			let lastDate: Date | null = null;
			let daysSinceLast: number | null = null;

			if (dates.length) {
				lastDate = new Date(
					Math.max.apply(
						null,
						dates.map((d) => d.getTime()),
					),
				);
				daysSinceLast = today.diff(dayjs(lastDate), "day");
			}

			let status: UserMeta["status"] = "ok";
			if (overdueCount > 0) {
				if (daysSinceLast != null && daysSinceLast >= 60) status = "risk";
				else status = "behind";
			}

			map.set(u.UserId, {
				userId: u.UserId,
				lastDate,
				daysSinceLast,
				overdueCount,
				status,
			});
		}

		return map;
	}, [safeItems]);

	const filteredUsers = useMemo(() => safeItems, [safeItems]);

	const handleToggleAll = () => {
		setExpandedPanels(
			allExpanded ? [] : safeItems.map((u) => `panel-${u.UserId}`),
		);
	};

	const handleToggle =
		(panelId: string) => (_e: React.SyntheticEvent, isExpanded: boolean) => {
			setExpandedPanels((prev) =>
				isExpanded ? [...prev, panelId] : prev.filter((id) => id !== panelId),
			);
		};

	const getCourseId = (session: { SessionId: number }): number => {
		const maybeSession = session as Record<string, unknown>;
		return typeof maybeSession.CourseId === "number"
			? maybeSession.CourseId
			: 0;
	};

	if (isLoading) {
		return <LinearProgress sx={{ width: "100%" }} />;
	}

	return (
		<Paper
			elevation={0}
			sx={{
				width: "100%",
				borderRadius: 3,
				border: (t) => `1px solid ${alpha(t.palette.divider, 0.7)}`,
				overflow: "hidden",
				background: theme.palette.background.paper,
			}}
		>
			{/* Header strip */}
			<Box
				sx={(t) => ({
					px: 2,
					py: 1.25,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					bgcolor:
						t.palette.mode === "dark"
							? alpha(t.palette.primary.main, 0.12)
							: alpha(t.palette.primary.main, 0.05),
					borderBottom: 1,
					borderColor: "divider",
				})}
			>
				<Box sx={{ minWidth: 0 }}>
					<Typography
						variant="overline"
						sx={{
							display: "block",
							color: "primary.main",
							fontWeight: 700,
							letterSpacing: "0.08em",
							textTransform: "uppercase",
						}}
					>
						<Translate String="Training.Dashboard.People">People</Translate>
					</Typography>
					<Stack direction="row" spacing={1} alignItems="center">
						<Typography
							variant="subtitle2"
							sx={{ fontWeight: 600, lineHeight: 1.2 }}
						>
							<Translate String="Training.Dashboard.EmergencyPersonnel">
								Emergency personnel
							</Translate>
						</Typography>
						{safeItems.length > 0 && (
							<Chip
								size="small"
								label={safeItems.length}
								sx={{
									borderRadius: 2,
									height: 20,
									bgcolor: alpha(theme.palette.primary.main, 0.08),
									color: "primary.main",
									fontWeight: 800,
								}}
							/>
						)}
					</Stack>
				</Box>
				<Button
					variant="outlined"
					startIcon={allExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
					onClick={handleToggleAll}
					size="small"
					sx={{
						borderRadius: 2,
						fontWeight: 700,
						textTransform: "none",
						px: 1.8,
					}}
				>
					{allExpanded ? "Collapse all" : "Expand all"}
				</Button>
			</Box>

			{/* Body */}
			{filteredUsers.length === 0 ? (
				<Box sx={{ py: 3, px: 2, textAlign: "center" }}>
					<Typography color="text.secondary" variant="body2" sx={{ mb: 0.5 }}>
						<Translate String="Training.Dashboard.NoPeopleSessions">
							No users or sessions available.
						</Translate>
					</Typography>
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{
							display: "inline-flex",
							px: 1.25,
							py: 1,
							borderRadius: 2,
							border: `1px dashed ${alpha(theme.palette.divider, 0.85)}`,
							bgcolor:
								theme.palette.mode === "dark"
									? alpha(theme.palette.common.white, 0.02)
									: alpha(theme.palette.primary.main, 0.02),
						}}
					>
						<Translate String="Training.Dashboard.NoPeopleSessionsHint">
							Assigned people will appear here once training is scheduled.
						</Translate>
					</Typography>
				</Box>
			) : (
				<List disablePadding>
					{filteredUsers.map((user, idx) => {
						const panelId = `panel-${user.UserId}`;
						const meta = metaById.get(user.UserId);
						const status = meta?.status ?? "ok";
						const statusLabel =
							status === "ok"
								? "OK"
								: status === "behind"
									? "Behind"
									: "At risk";
						const statusColor =
							status === "ok"
								? theme.palette.success.main
								: status === "behind"
									? theme.palette.warning.main
									: theme.palette.error.main;
						const lastDateLabel =
							meta?.lastDate != null
								? dayjs(meta.lastDate).format("DD/MM/YY")
								: "-";
						return (
							<Box key={panelId} mb={idx < safeItems.length - 1 ? 1.5 : 0}>
								<Accordion
									expanded={expandedPanels.includes(panelId)}
									onChange={handleToggle(panelId)}
									disableGutters
									elevation={0}
									sx={{
										borderRadius: 1.5,
										border: (t) => `1px solid ${t.palette.divider}`,
										"&:before": { display: "none" },
										overflow: "hidden",
									}}
								>
									<AccordionSummary
										expandIcon={<ExpandMoreIcon />}
										aria-controls={`${panelId}-content`}
										id={`${panelId}-header`}
										sx={{
											bgcolor:
												theme.palette.mode === "light"
													? alpha(theme.palette.primary.main, 0.04)
													: alpha(theme.palette.primary.main, 0.12),
										}}
									>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												width: "100%",
												gap: 1.5,
											}}
										>
											<Box sx={{ minWidth: 0 }}>
												<Typography variant="subtitle1" fontWeight={700}>
													{user.Name}
												</Typography>
												<Typography
													variant="caption"
													color="text.secondary"
													sx={{ display: "block", mt: 0.25 }}
												>
													Last training: {lastDateLabel} • Overdue:{" "}
													{meta?.overdueCount ?? 0}
												</Typography>
											</Box>
											<Chip
												size="small"
												label={statusLabel}
												sx={{
													borderRadius: 2,
													height: 22,
													bgcolor: alpha(statusColor, 0.08),
													color: statusColor,
													border: `1px solid ${alpha(statusColor, 0.6)}`,
												}}
											/>
										</Box>
									</AccordionSummary>

									<AccordionDetails sx={{ p: 0 }}>
										{user.Sessions.length ? (
											<List disablePadding>
												{user.Sessions.map((s, sidx) => {
													const d = new Date(s.SessionDate);
													const daysAgo = Math.floor(
														(Date.now() - d.getTime()) / (1000 * 60 * 60 * 24),
													);
													const isOverdue = daysAgo > 0;
													const courseId = getCourseId(s);
													return (
														<React.Fragment key={s.SessionId}>
															<ListItemButton
																component={Link}
																to={`/training/courses/${courseId}/sessions/${s.SessionId}`}
																sx={{
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "space-between",
																	p: 2,
																	bgcolor: isOverdue
																		? alpha(theme.palette.error.main, 0.04)
																		: "transparent",
																	"&:hover": {
																		background: alpha(
																			theme.palette.primary.main,
																			0.06,
																		),
																	},
																}}
															>
																<Typography
																	variant="body1"
																	fontWeight={600}
																	sx={{ flex: 2 }}
																>
																	{s.CourseName}
																</Typography>

																<Typography
																	variant="body2"
																	color="text.secondary"
																	sx={{ flex: 1, textAlign: "center" }}
																>
																	ID: {s.SessionId}
																</Typography>

																<Typography
																	variant="body2"
																	color="text.secondary"
																	sx={{ flex: 2, textAlign: "center" }}
																>
																	{dayjs(d).format("DD/MM/YY")}
																</Typography>

																<Typography
																	variant="body2"
																	fontWeight={600}
																	sx={{ flex: 1, textAlign: "center" }}
																>
																	{daysAgo} d ago
																</Typography>

																{s.Reason && (
																	<Tooltip title={s.Reason}>
																		<InfoOutlinedIcon
																			fontSize="small"
																			sx={{ color: "text.secondary", mx: 1 }}
																		/>
																	</Tooltip>
																)}

																<Tooltip title="Open details">
																	<LaunchIcon fontSize="small" />
																</Tooltip>
															</ListItemButton>
															{sidx < user.Sessions.length - 1 && (
																<Divider component="li" sx={{ mx: 2 }} />
															)}
														</React.Fragment>
													);
												})}
											</List>
										) : (
											<Typography
												py={2}
												textAlign="center"
												color="text.secondary"
											>
												<Translate String="Training.Dashboard.NoUserSessions">
													No sessions available.
												</Translate>
											</Typography>
										)}
									</AccordionDetails>
								</Accordion>
							</Box>
						);
					})}
				</List>
			)}
		</Paper>
	);
};

export default SessionsAccordion;
